import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Upload, FileText } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../lib/api";
import { PHONE_CODES, STATUS_IN_CANADA, PROGRAMS } from "../lib/data";
import { toast } from "sonner";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-slate-700">
      {label} {required && <span className="text-[#C49A3A]">*</span>}
    </span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className={
      "w-full rounded-md border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 input-focus " +
      (props.className || "")
    }
  />
);

const Select = ({ children, ...props }) => (
  <select
    {...props}
    className={
      "w-full rounded-md border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 input-focus " +
      (props.className || "")
    }
  >
    {children}
  </select>
);

export default function Apply() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    phone_code: "+1",
    date_of_birth: "",
    nationality: "",
    status_in_canada: "",
    program: "",
  });
  const [idDoc, setIdDoc] = useState(null);
  const [supDoc, setSupDoc] = useState(null);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const canContinue =
    form.full_name &&
    form.email &&
    form.phone &&
    form.phone_code &&
    form.date_of_birth &&
    form.nationality &&
    form.status_in_canada;

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      if (idDoc) fd.append("id_document", idDoc);
      if (supDoc) fd.append("supporting_document", supDoc);
      const { data } = await api.post("/apply", fd);
      setSubmitted(data);
      toast.success("Application submitted successfully");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit application");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-cct-ambient">
        <Header minimal />
        <main className="max-w-2xl mx-auto px-4 py-20 text-center fade-up">
          <div className="inline-flex w-16 h-16 rounded-full bg-[#1E3A8A]/10 items-center justify-center">
            <CheckCircle2 className="w-9 h-9 text-cct-blue" />
          </div>
          <h1 className="mt-6 font-display text-4xl font-semibold text-cct-blue">
            Application Submitted
          </h1>
          <p className="mt-3 text-slate-600">
            Thank you! Our admissions team will review your application and be in touch within one business day.
          </p>
          <div className="mt-6 inline-block bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm">
            <span className="text-slate-500">Reference ID:</span>{" "}
            <span className="font-mono text-slate-900">{submitted.id}</span>
          </div>
          <div className="mt-10">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-cct-blue hover:underline"
            >
              <ArrowLeft className="w-4 h-4" /> Back to portal
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cct-ambient">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pb-20">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-cct-blue mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to portal
        </Link>
        <div className="text-center fade-up">
          <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-gold">
            Student Application
          </div>
          <h1 className="mt-3 font-display text-5xl font-semibold text-cct-blue">
            Apply Now
          </h1>
          <p className="mt-3 text-slate-600">
            A short form, then document upload. Takes less than 2 minutes.
          </p>
        </div>

        {/* Stepper */}
        <div className="mt-10 flex items-center justify-center gap-3 text-sm">
          {[1, 2].map((n) => (
            <React.Fragment key={n}>
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                  step >= n
                    ? "bg-cct-blue text-white"
                    : "bg-slate-200 text-slate-500"
                }`}
              >
                {n}
              </div>
              <div
                className={
                  step >= n ? "text-cct-blue font-medium" : "text-slate-500"
                }
              >
                {n === 1 ? "Your Details" : "Upload Documents"}
              </div>
              {n === 1 && <div className="w-10 h-px bg-slate-300" />}
            </React.Fragment>
          ))}
        </div>

        <div className="mt-10 bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8 fade-up-1">
          {step === 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <Field label="Full Name" required>
                  <Input
                    value={form.full_name}
                    onChange={(e) => update("full_name", e.target.value)}
                    placeholder="Jane Doe"
                  />
                </Field>
              </div>
              <Field label="Email" required>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@example.com"
                />
              </Field>
              <Field label="Phone" required>
                <div className="flex gap-2">
                  <Select
                    value={form.phone_code}
                    onChange={(e) => update("phone_code", e.target.value)}
                    className="w-28"
                  >
                    {PHONE_CODES.map((p) => (
                      <option key={p.region} value={p.code}>
                        {p.region} {p.code}
                      </option>
                    ))}
                  </Select>
                  <Input
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="604 123 4567"
                  />
                </div>
              </Field>
              <Field label="Date of Birth" required>
                <Input
                  type="date"
                  value={form.date_of_birth}
                  onChange={(e) => update("date_of_birth", e.target.value)}
                />
              </Field>
              <Field label="Nationality" required>
                <Input
                  value={form.nationality}
                  onChange={(e) => update("nationality", e.target.value)}
                  placeholder="Canadian"
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Status in Canada" required>
                  <Select
                    value={form.status_in_canada}
                    onChange={(e) => update("status_in_canada", e.target.value)}
                  >
                    <option value="">Select status</option>
                    {STATUS_IN_CANADA.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Program of Interest (optional)">
                  <Select
                    value={form.program}
                    onChange={(e) => update("program", e.target.value)}
                  >
                    <option value="">— Select a program —</option>
                    {PROGRAMS.map((p) => (
                      <option
                        key={`${p.name}-${p.type}`}
                        value={`${p.name} — ${p.type}`}
                      >
                        {p.name} — {p.type}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
              <div className="sm:col-span-2 mt-2 flex justify-end">
                <button
                  disabled={!canContinue}
                  onClick={() => setStep(2)}
                  className="inline-flex items-center gap-2 bg-cct-blue text-white font-medium px-5 py-2.5 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#1a3380] transition-colors"
                >
                  Continue to Document Upload <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <FileUpload
                label="Government-issued ID"
                hint="Passport, driving license, or national ID"
                file={idDoc}
                onFile={setIdDoc}
              />
              <FileUpload
                label="Supporting Document (optional)"
                hint="Transcripts, resume, or any supporting paperwork"
                file={supDoc}
                onFile={setSupDoc}
                optional
              />
              <div className="flex justify-between items-center pt-4">
                <button
                  onClick={() => setStep(1)}
                  className="text-sm text-slate-600 hover:text-cct-blue inline-flex items-center gap-1"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                  onClick={onSubmit}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 bg-cct-blue text-white font-medium px-5 py-2.5 rounded-md hover:bg-[#1a3380] transition-colors disabled:opacity-60"
                >
                  {submitting ? "Submitting..." : "Submit Application"}
                  {!submitting && <ArrowRight className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function FileUpload({ label, hint, file, onFile, optional }) {
  return (
    <div className="border border-dashed border-slate-300 rounded-xl p-5 bg-slate-50/60">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-slate-800">
            {label}{" "}
            {optional ? (
              <span className="text-slate-400 font-normal">Optional</span>
            ) : (
              <span className="text-[#C49A3A]">Required</span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
          <div className="text-[11px] text-slate-400 mt-1">
            PDF, JPG, or PNG · up to 10 MB
          </div>
        </div>
        <label className="inline-flex items-center gap-2 bg-white border border-slate-300 px-3.5 py-2 rounded-md text-sm font-medium text-slate-700 hover:border-cct-blue hover:text-cct-blue cursor-pointer transition-colors">
          <Upload className="w-4 h-4" /> Upload
          <input
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => onFile(e.target.files?.[0] || null)}
          />
        </label>
      </div>
      {file && (
        <div className="mt-3 flex items-center gap-2 text-sm bg-white border border-slate-200 rounded-md px-3 py-2">
          <FileText className="w-4 h-4 text-cct-blue" />
          <span className="text-slate-700 truncate">{file.name}</span>
          <button
            onClick={() => onFile(null)}
            className="ml-auto text-xs text-slate-500 hover:text-red-500"
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}
